import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import * as moment from 'moment';
import {
  CreateElectricalMeasurementAddMeasurement,
  CreateElectricalMeasurementClear,
  CreateElectricalMeasurementDeleteMeasurementAtIndex,
  CreateElectricalMeasurementSetBase,
  CreateElectricalMeasurementSetProject,
  CreateElectricalMeasurementUpdateMeasurementAtIndex,
} from 'src/app/ngxs/create-electrical-measurement/create-electrical-measurement.actions';
import {
  CreateElectricalMeasurementStateModel,
} from 'src/app/ngxs/create-electrical-measurement/create-electrical-measurement.state.model';
import { WorkElectricalMeasurement, WorkProject } from '../../_generated/work-rest-api';

const STATE_NAME = 'axova__createElectricalMeasurement';
const DEFAULT_STATE: CreateElectricalMeasurementStateModel = {
  baseMeasurement: {
    electricalMeasurementTypeId: undefined,
    electricalMeasurementDeviceId: undefined,
    created: moment().toISOString(),
  },
  measurements: [],
  project: undefined,
};

@State<CreateElectricalMeasurementStateModel>({
  name: STATE_NAME,
  defaults: DEFAULT_STATE,
})
@Injectable()
export class CreateElectricalMeasurementState {
  @Selector()
  static baseMeasurement(state: CreateElectricalMeasurementStateModel): Partial<WorkElectricalMeasurement> {
    return state.baseMeasurement;
  }

  @Selector()
  static project(state: CreateElectricalMeasurementStateModel): WorkProject {
    return state.project;
  }

  @Selector()
  static measurements(state: CreateElectricalMeasurementStateModel): Partial<WorkElectricalMeasurement>[] {
    return state.measurements;
  }

  @Selector()
  static aggregatedMeasurements(state: CreateElectricalMeasurementStateModel): Partial<WorkElectricalMeasurement>[] {
    const baseMeasurement = state.baseMeasurement;
    return state.measurements.map(measurement => {
      return {
        ...baseMeasurement,
        ...measurement,
        projectId: state.project.id,
      };
    });
  }

  @Action(CreateElectricalMeasurementSetProject)
  setProject(ctx: StateContext<CreateElectricalMeasurementStateModel>, action: CreateElectricalMeasurementSetProject) {
    ctx.patchState({ project: action.project });
  }

  @Action(CreateElectricalMeasurementSetBase)
  setBase(ctx: StateContext<CreateElectricalMeasurementStateModel>, action: CreateElectricalMeasurementSetBase) {
    ctx.patchState({ baseMeasurement: action.electricalMeasurement });
  }

  @Action(CreateElectricalMeasurementAddMeasurement)
  addMeasurement(ctx: StateContext<CreateElectricalMeasurementStateModel>, action: CreateElectricalMeasurementAddMeasurement) {
    ctx.patchState({ measurements: ctx.getState().measurements.concat(action.electricalMeasurement) });
  }

  @Action(CreateElectricalMeasurementUpdateMeasurementAtIndex)
  updateMeasurementAtIndex(
    ctx: StateContext<CreateElectricalMeasurementStateModel>,
    action: CreateElectricalMeasurementUpdateMeasurementAtIndex,
  ) {
    const measurements = JSON.parse(JSON.stringify(ctx.getState().measurements));
    measurements[action.indexOfMeasurement] = action.electricalMeasurement;
    ctx.patchState({ measurements });
  }

  @Action(CreateElectricalMeasurementDeleteMeasurementAtIndex)
  deleteMeasurementAtIndex(
    ctx: StateContext<CreateElectricalMeasurementStateModel>,
    action: CreateElectricalMeasurementDeleteMeasurementAtIndex,
  ) {
    const measurements = JSON.parse(JSON.stringify(ctx.getState().measurements)) as WorkElectricalMeasurement[];
    measurements.splice(action.indexOfMeasurement, 1);
    ctx.patchState({ measurements });
  }

  @Action(CreateElectricalMeasurementClear)
  clear(ctx: StateContext<CreateElectricalMeasurementStateModel>, action: CreateElectricalMeasurementClear) {
    ctx.setState(DEFAULT_STATE);
  }
}
